import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { queryClient } from './lib/queryClient.js';
import { router } from './router.js';
import { UndoSendToastHost } from './components/UndoSendToast.js';
import { installModalBackdropGuard } from './lib/modalBackdropGuard.js';
import './styles/tokens.css';
import './styles/glass.css';
import './features/settings/themePreference.js';

// Stop a text-selection drag that ends on a modal backdrop from closing
// the modal (the stray click lands on the backdrop). One document-level
// listener covers every modal in the app.
installModalBackdropGuard();

const el = document.getElementById('root');
if (!el) throw new Error('#root not found');

createRoot(el).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <UndoSendToastHost />
    </QueryClientProvider>
  </StrictMode>,
);

// Warm every route's lazy chunk shortly after the first paint. Kills
// the Suspense-fallback flash that used to appear on the first click
// into each module — by the time the agent hits a sidebar link the
// chunk is already parsed. Uses requestIdleCallback so prefetching
// never contends with initial-paint work; falls back to a short
// setTimeout on Safari which doesn't expose rIC.
type IdleWindow = Window & { requestIdleCallback?: (cb: () => void) => number };
const w = window as IdleWindow;
const prefetch = () => {
  void import('./routePreloaders.js').then((module) => module.preloadAllRoutes());
};
if (typeof w.requestIdleCallback === 'function') {
  w.requestIdleCallback(prefetch);
} else {
  setTimeout(prefetch, 1200);
}
